// Shared Chart Tooltip Component
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";
import { getColorClassName, type AvailableChartColorsKeys } from "@/lib/chartUtils";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

//#region Tooltip

type TooltipProps = Pick<ChartTooltipProps, "active" | "payload" | "label">;

type PayloadItem = {
  category: string;
  value: number;
  index: string;
  color: AvailableChartColorsKeys;
  type?: string;
  payload: any;
  /** Metric key for per-metric value formatting (e.g. prefix/suffix) */
  metricKey?: string;
};

interface ChartTooltipProps {
  active: boolean | undefined;
  payload: PayloadItem[];
  label: string;
  valueFormatter: (value: number, payloadItem?: PayloadItem) => string;
}

export const ChartTooltip = ({
  active,
  payload,
  label,
  valueFormatter,
}: ChartTooltipProps) => {
  const [isScrollable, setIsScrollable] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkScrollable = () => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
      // Only show gradient if not at the bottom and has scrollable content
      const hasScrollableContent = scrollHeight > clientHeight;
      const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
      setIsScrollable(hasScrollableContent && !isAtBottom);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (active && payload?.length) {
      // Use setTimeout to ensure the DOM is updated
      timeoutId = setTimeout(checkScrollable, 0);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Reset scrollable state when tooltip hides
      if (!active) {
        setIsScrollable(false);
      }
    };
  }, [active, payload]);
  if (active && payload && payload.length) {
    const legendPayload = payload.filter((item: PayloadItem) => item.type !== "none");
    return (
      <div
        className={cn(
          // base
          "rounded-md border text-sm shadow-md",
          // border color
          "border-gray-200 dark:border-gray-800",
          // background color
          "bg-white dark:bg-gray-950",
          "w-min-[248px] max-w-[400px]",
          "pointer-events-auto overscroll-contain",
        )}
      >
        <div className={cn("border-b border-inherit px-4 py-2")}>
          <p
            className={cn(
              "break-words whitespace-normal",
              "font-inter",
              "font-medium",
              "not-italic",
              "text-[14px]",
              "leading-5",
              "text-gray-700"
            )}
          >
            {label}
          </p>
        </div>
        <div
          className={cn("relative py-2 max-h-[300px] overflow-y-auto")}
          ref={contentRef}
          onScroll={checkScrollable}
        >
          {legendPayload.map((item, index) => {
            const { value, category, color } = item;
            return (
            <div
              key={`id-${index}`}
              className="relative flex items-center justify-between pl-4 pr-4"
            >
              {/* Vertical color bar on left edge */}
              <div
                aria-hidden="true"
                className={cn(
                  "absolute left-0 top-0 bottom-0 w-1 rounded-r-md",
                  typeof color === "string" &&
                    !color.startsWith("#") &&
                    !color.startsWith("rgba")
                    ? getColorClassName(color, "bg")
                    : undefined,
                )}
                style={{
                  backgroundColor:
                    typeof color === "string" &&
                    (color.startsWith("#") || color.startsWith("rgba"))
                      ? color
                      : undefined,
                }}
              />
              <div className="w-full flex items-center justify-between gap-2">
                <p
                  className={cn(
                    "flex-1 text-left font-medium not-italic whitespace-normal break-words",
                    "text-[12px] leading-5",
                    "[color:var(--Blue-500,#67749B)]",
                    "font-inter"
                  )}
                >
                  {category}
                </p>
                <p
                  className={cn(
                    "text-right font-medium whitespace-nowrap tabular-nums text-[12px] leading-[20px] translate-y-[1px]",
                    "text-[#374151] dark:text-gray-50",
                    "not-italic"
                  )}
                >
                  {value === null || value === undefined || isNaN(value)
                    ? "—"
                    : valueFormatter(value, item)}
                </p>
              </div>
             
            </div>
          );
          })}
        </div>
        {isScrollable && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[42px] pointer-events-none rounded-b-md"
            style={{
              background:
                "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
      </div>
    );
  }
  return null;
};

export type { TooltipProps, PayloadItem, ChartTooltipProps };
